import React, { useEffect, useState } from "react";
import Select, { components } from "react-select";
import { useGetUserCompanies } from "../../../../backend/query";
import { FULL_USER_COMPANIES_PAYLOAD } from "../../../../backend/license";

interface LicenseFiltersProps {
  onChange: (filters: any) => void;
  showStateCityInsteadOfLocation?: boolean;
}

const ALL_OPTION = {
  value: "all",
  label: "ALL",
  company: null,
  associate_company: null,
  location: null,
  state: null,
  city: null,
};

const TAG_OPTIONS = [
  { value: "all", label: "ALL" },
  { value: "due", label: "Due" },
  { value: "overdue", label: "Overdue" },
];

function NoticeMangementFilters({
  onChange,
  showStateCityInsteadOfLocation = false,
}: LicenseFiltersProps) {
  const [company, setCompany] = useState<any>(ALL_OPTION);
  const [associateCompany, setAssociateCompany] = useState<any>(ALL_OPTION);
  const [location, setLocation] = useState<any>(ALL_OPTION);
  const [state, setState] = useState<any>(ALL_OPTION);
  const [city, setCity] = useState<any>(ALL_OPTION);
  const [tag, setTag] = useState<any>(TAG_OPTIONS[0]);
  const [allCompanyIds, setAllCompanyIds] = useState<number[]>([]);

  const [companies, setCompanies] = useState<any[]>([]);
  const [associateCompanies, setAssociateCompanies] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);

  const { userCompanies, isFetching } = useGetUserCompanies(FULL_USER_COMPANIES_PAYLOAD, true);

  useEffect(() => {
    if (userCompanies && userCompanies.length > 0) {
      const companyOptions = [
        ALL_OPTION,
        ...userCompanies.map(function (c: any) {
          return { value: c.id, label: c.name, company: c };
        }),
      ];
      setCompanies(companyOptions);
      setAllCompanyIds(userCompanies.map((c: any) => c.id));
    }
  }, [userCompanies]);

  useEffect(() => {
    if (company && company.value === "all") {
      setAssociateCompanies([ALL_OPTION]);
      setAssociateCompany(ALL_OPTION);
      setLocations([ALL_OPTION]);
      setLocation(ALL_OPTION);
      setStates([ALL_OPTION]);
      setState(ALL_OPTION);
      setCities([ALL_OPTION]);
      setCity(ALL_OPTION);
    } else if (company && company.company) {
      const assocCompanies = [
        ALL_OPTION,
        ...(company.company.associate_companies || []).map(function (ac: any) {
          return {
            value: ac.id,
            label: ac.name,
            associate_company: ac,
            locations: ac.locations || [],
          };
        }),
      ];
      setAssociateCompanies(assocCompanies);
      setAssociateCompany(ALL_OPTION);
      setLocations([ALL_OPTION]);
      setLocation(ALL_OPTION);
      setStates([ALL_OPTION]);
      setState(ALL_OPTION);
      setCities([ALL_OPTION]);
      setCity(ALL_OPTION);
    } else {
      setAssociateCompanies([]);
      setAssociateCompany(null);
      setLocations([]);
      setLocation(null);
      setStates([]);
      setState(null);
      setCities([]);
      setCity(null);
    }
  }, [company]);

  useEffect(() => {
    if (associateCompany && associateCompany.value === "all") {
      setLocations([ALL_OPTION]);
      setLocation(ALL_OPTION);
      setStates([ALL_OPTION]);
      setState(ALL_OPTION);
      setCities([ALL_OPTION]);
      setCity(ALL_OPTION);
    } else if (associateCompany && associateCompany.locations) {
      const locationOptions = [
        ALL_OPTION,
        ...associateCompany.locations.map(function (loc: any) {
          const label =
            loc.city && loc.city.name
              ? loc.name + ", " + loc.city.name
              : loc.name;
          return { value: loc.id, label: label, location: loc };
        }),
      ];
      setLocations(locationOptions);
      setLocation(ALL_OPTION);

      if (showStateCityInsteadOfLocation) {
        const statesMap = new Map();
        associateCompany.locations.forEach((loc: any) => {
          if (loc.state && loc.state.id) {
            statesMap.set(loc.state.id, loc.state);
          }
        });

        const stateOptions = [
          ALL_OPTION,
          ...Array.from(statesMap.values()).map((state: any) => ({
            value: state.id,
            label: state.name,
            state: state,
          })),
        ];
        setStates(stateOptions);
        setState(ALL_OPTION);
      } else {
        setStates([ALL_OPTION]);
        setState(ALL_OPTION);
      }
      setCities([ALL_OPTION]);
      setCity(ALL_OPTION);
    }
  }, [associateCompany,showStateCityInsteadOfLocation]);

  useEffect(() => {
    if (location && location.value === "all") {
      setStates([ALL_OPTION]);
      setState(ALL_OPTION);
      setCities([ALL_OPTION]);
      setCity(ALL_OPTION);
    } else if (location && location.location && location.location.state) {
      const stateData = location.location.state;
      const stateOption = [
        ALL_OPTION,
        { value: stateData.id, label: stateData.name, state: stateData },
      ];
      setStates(stateOption);
      setState(ALL_OPTION);
      setCities([ALL_OPTION]);
      setCity(ALL_OPTION);
    } else {
      setStates([]);
      setState(null);
      setCities([]);
      setCity(null);
    }
  }, [location]);

  useEffect(() => {
    if (state && state.value === "all") {
      setCities([ALL_OPTION]);
      setCity(ALL_OPTION);
    } else if (state && state.state && state.state.cities) {
      const cityOptions = [
        ALL_OPTION,
        ...state.state.cities.map(function (c: any) {
          return { value: c.id, label: c.name, city: c };
        }),
      ];
      setCities(cityOptions);
      setCity(ALL_OPTION);
    } else if (showStateCityInsteadOfLocation && state && state.state && associateCompany && associateCompany.locations) {
      const citiesMap = new Map();
      associateCompany.locations.forEach((loc: any) => {
        if (loc.state && loc.state.id === state.value && loc.city && loc.city.id) {
          citiesMap.set(loc.city.id, loc.city);
        }
      });

      const cityOptions = [
        ALL_OPTION,
        ...Array.from(citiesMap.values()).map((city: any) => ({
          value: city.id,
          label: city.name,
          city: city
        }))
      ];
      setCities(cityOptions);
      setCity(ALL_OPTION);
    } else if (location && location.location && location.location.city) {
      const cityData = location.location.city;
      setCities([
        ALL_OPTION,
        { value: cityData.id, label: cityData.name, city: cityData },
      ]);
      setCity(ALL_OPTION);
    } else {
      setCities([]);
      setCity(null);
    }
  }, [state, showStateCityInsteadOfLocation, associateCompany]);

  useEffect(() => {
    const filters: any = {};
    if (company && company.value === "all") {
      if (allCompanyIds && allCompanyIds.length > 0) {
        filters.company_id_in = allCompanyIds;
      }
    } else if (company && company.value !== "all") {
      filters.company_id_eq = company.value;
    }
    if (associateCompany && associateCompany.value !== "all") {
      filters.associate_company_id_eq = associateCompany.value;
    }
    if (location && location.value !== "all") {
      filters.location_id_eq = location.value;
    }
    if (state && state.value !== "all") {
      filters.state_id_eq = state.value;
    }
    if (city && city.value !== "all") {
      filters.city_id_eq = city.value;
    }
    if (tag && tag.value !== "all") {
      filters.tag = tag.value;
    }
    onChange(filters);
  }, [company, associateCompany, location, state, city, tag, allCompanyIds]);

  const DropdownIndicator = function (props: any) {
    return (
      <components.DropdownIndicator {...props}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="10"
          height="6"
          viewBox="0 0 10 6"
          fill="none"
        >
          <path
            d="M1 1L5 5L9 1"
            stroke="#1E1E1E"
            strokeWidth="1.33333"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </components.DropdownIndicator>
    );
  };

  return (
    <>
      <div className="px-2">
        <label className="filter-label">
          <small>Company</small>
        </label>
        <Select
          placeholder="Company"
          options={companies}
          onChange={setCompany}
          value={company}
          className="select-control"
          components={{ DropdownIndicator }}
          isLoading={isFetching}
          isSearchable={true}
        />
      </div>

      <div className="px-2">
        <label className="filter-label">
          <small>Associate Company</small>
        </label>
        <Select
          placeholder="Associate Company"
          options={associateCompanies}
          onChange={setAssociateCompany}
          value={associateCompany}
          className="select-control"
          components={{ DropdownIndicator }}
          isDisabled={!company || company.value === "all"}
          isSearchable={true}
        />
      </div>

      {!showStateCityInsteadOfLocation && (
        <div className="px-2">
          <label className="filter-label">
            <small>Location</small>
          </label>
          <Select
            placeholder="Location"
            options={locations}
            onChange={setLocation}
            value={location}
            className="select-control"
            components={{ DropdownIndicator }}
            isDisabled={!associateCompany || associateCompany.value === "all"}
            isSearchable={true}
          />
        </div>
      )}

      {showStateCityInsteadOfLocation && (
        <>
          <div className="px-2">
            <label className="filter-label">
              <small>State</small>
            </label>
            <Select
              placeholder="State"
              options={states}
              onChange={setState}
              value={state}
              className="select-control"
              components={{ DropdownIndicator }}
              isDisabled={!associateCompany || associateCompany.value === "all"}
              isSearchable={true}
            />
          </div>

          <div className="px-2">
            <label className="filter-label">
              <small>City</small>
            </label>
            <Select
              placeholder="City"
              options={cities}
              onChange={setCity}
              value={city}
              className="select-control"
              components={{ DropdownIndicator }}
              isDisabled={!state || state.value === "all"}
              isSearchable={true}
            />
          </div>
        </>
      )}

      <div className="px-2">
        <label className="filter-label">
          <small>Tag</small>
        </label>
        <Select
          placeholder="Tag"
          options={TAG_OPTIONS}
          onChange={setTag}
          value={tag}
          className="select-control"
          components={{ DropdownIndicator }}
          isSearchable={false}
        />
      </div>
    </>
  );
}

export default NoticeMangementFilters;
